import React, { useEffect, useMemo } from 'react';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { selectCurrentUser, selectIconPack } from '@shared/store/auth.duck';
import { TeamMovementResponse, WorkgroupWhereabouts } from '@shared/models/api.models';
import { MemberMovements } from '@features/whereabouts/models/movements.models';
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { useTranslation } from "react-i18next";
import { UNKNOWN_OPTION } from '@features/whereabouts/services/WhereaboutOptions';
import { TeamMultiSelectWithFavourites } from '@shared/ui/organisms/team-drop-down-list/TeamMultiSelectWithFavourites';
import { selectAllTeams } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import { DailyMemberRow } from './DailyMemberRow';
import { DailyCurrentUserSection } from './DailyCurrentUserSection';
import {
  ColumnBackgrounds,
  ColumnBg,
  Container,
  ContentLayer,
  EmptyState,
  TeamSelectSection,
} from './WhereaboutsV2Styles';
import { DailyViewHeader } from "@features/whereabouts/pages/whereabouts-v2/DailyViewHeader";
import { selectDailyFilter } from "@features/whereabouts/store/whereaboutsView.duck";
import { useOfficeNameMap } from '@features/whereabouts/hooks/useOfficeNameMap';
import { useWhereaboutsOptionResolver } from '@features/whereabouts/hooks/useWhereaboutsOptionResolver';
import { useScopedGroupIds } from '@features/whereabouts/hooks/useScopedGroupIds';
import { useScopedGroups } from '@features/whereabouts/hooks/useScopedGroups';
import { excludeUser } from '@features/whereabouts/models/whereaboutsScope';
import EmptyTeamView from '@shared/ui/organisms/movements-wallchart/components/empty-team-view/EmptyTeamView';
import EmptyTagView from '@shared/ui/organisms/movements-wallchart/components/empty-team-view/EmptyTagView';
import { useWhereaboutsScope } from './WhereaboutsScopeContext';
import { matchesDailyFilter } from './whereaboutsFilters';
import { FilteredGroupEmptyMessage, WhereaboutsGroupSection } from './WhereaboutsGroupSection';
import { CheckinConfirmationsContext, useLoadCheckinConfirmations } from './CheckinConfirmations';
import { WithWorkPlannerTeamEvents } from '@features/wall-planner/hooks/WithWorkPlannerTeamEvents';
import { TeamEventsRow } from '@features/wall-planner/components/TeamEventsRow';
import {
  clearDailyData,
  loadCurrentUserDailyData,
  loadDailyWhereabouts,
  selectDailyCurrentUserData,
  selectDailyData,
  selectDailyLoading,
  setDailyLoading,
} from "@features/whereabouts/store/dailyWhereabouts.duck";

export function DailyWhereaboutsView() {
  const filter = useSelector(selectDailyFilter);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { scope, isScoped } = useWhereaboutsScope();
  const calendarDate = useSelector(selectCalendarDate);
  const iconPack = useSelector(selectIconPack);
  const currentUser = useSelector(selectCurrentUser);
  const allTeams = useSelector(selectAllTeams);
  const allWorkGroups = useSelector(selectAllTags);
  const data = useSelector(selectDailyData);
  const loading = useSelector(selectDailyLoading);
  const currentUserData = useSelector(selectDailyCurrentUserData);

  const { teamIds, workGroupIds } = useScopedGroupIds(scope);
  const scopedGroups = useScopedGroups(scope);
  const officeNameMap = useOfficeNameMap();
  const getOption = useWhereaboutsOptionResolver();
  WithWorkPlannerTeamEvents(scopedGroups.teams);
  const dayInView = useMemo(() => calendarDate ? [calendarDate] : [], [calendarDate]);
  const checkins = useLoadCheckinConfirmations(dayInView);

  useEffect(() => {
    if (!calendarDate || !currentUser?.id) return;
    dispatch(loadCurrentUserDailyData({ calendarDate, userId: currentUser.id }));
  }, [dispatch, calendarDate, currentUser?.id]);

  useEffect(() => {
    if (!calendarDate) return;

    if (teamIds.length === 0 && workGroupIds.length === 0) {
      dispatch(clearDailyData());
      return;
    }

    dispatch(setDailyLoading(true));
    let loadStarted = false;
    const timeout = setTimeout(() => {
      loadStarted = true;
      dispatch(loadDailyWhereabouts({
        date: calendarDate.format(DATE_FORMAT),
        teamIds,
        workGroupIds,
      }));
    }, 300);

    return () => {
      clearTimeout(timeout);
      // If the debounced load never fired, nothing will clear the spinner.
      if (!loadStarted) dispatch(setDailyLoading(false));
    };
  }, [dispatch, calendarDate, teamIds, workGroupIds]);

  if (loading) {
    return <LoadingContainer><SimpleSpinner size={32} /></LoadingContainer>;
  }

  const hasTeamData = data && (data.teamWhereabouts.length > 0 || data.workgroupsWhereabouts.length > 0);
  const dateStr = calendarDate?.format(DATE_FORMAT) || '';

  const renderMembers = (members: MemberMovements[]) =>
    members.filter(Boolean).map(member => {
      const dayData = (member.whereabouts ?? []).find(w => w.date === dateStr);
      const amOption = dayData ? getOption(dayData.amStatusId) : UNKNOWN_OPTION;
      const pmOption = dayData ? getOption(dayData.pmStatusId) : UNKNOWN_OPTION;

      if (!matchesDailyFilter(filter, amOption, pmOption)) return null;

      return (
        <DailyMemberRow key={member.id}
                        member={member}
                        dayData={dayData}
                        amOption={amOption}
                        pmOption={pmOption}
                        officeNameMap={officeNameMap}
                        iconPack={iconPack}
                        dateStr={dateStr} />
      );
    });

  // The signed-in user has their own section at the top, so they are left out
  // of every group list.
  const renderTeamBody = (members: MemberMovements[]) => {
    const rendered = renderMembers(excludeUser(members, currentUser?.id));
    if (filter && rendered.filter(Boolean).length === 0) {
      return <FilteredGroupEmptyMessage period="today" />;
    }
    return rendered;
  };

  const emptyStateKey = isScoped ? 'whereabouts-v2.group-not-found' : 'whereabouts-v2.no-groups-selected';

  return (
    <CheckinConfirmationsContext.Provider value={checkins}>
      <Container>
        <ColumnBackgrounds $columns={2}>
            <ColumnBg $even={true} $isLast={false} />
            <ColumnBg $even={false} $isLast={true} />
          </ColumnBackgrounds>

          <ContentLayer>
            <DailyViewHeader />

            <DailyCurrentUserSection userId={currentUser?.id ?? ''}
                                     firstname={currentUser?.firstname ?? ''}
                                     lastname={currentUser?.lastname ?? ''}
                                     currentUserData={currentUserData}
                                     getOption={getOption}
                                     officeNameMap={officeNameMap}
                                     iconPack={iconPack}
                                     dateStr={dateStr} />

            {!isScoped && (
              <TeamSelectSection>
                <TeamMultiSelectWithFavourites teams={allTeams}
                                               workGroups={allWorkGroups}
                                               outerClassName={'multiSelect__wrapper'}
                                               multiSelectClassName={'multiSelect__dropDown'}
                                               reloadTeamWhereaboutsOnSelect={true} />
              </TeamSelectSection>
            )}

            {hasTeamData ? (
              <>
                {data.teamWhereabouts.map((team: TeamMovementResponse) => (
                  <WhereaboutsGroupSection key={team.teamEntity.id} title={team.teamEntity.name} restricted={team.restricted}>
                    <TeamEventsRow teamId={team.teamEntity.id} days={dayInView} />
                    {isScoped && (team.teamWhereabouts ?? []).length === 0
                      ? <EmptyTeamView team={team.teamEntity} />
                      : renderTeamBody(team.teamWhereabouts || [])}
                  </WhereaboutsGroupSection>
                ))}

                {data.workgroupsWhereabouts.map((wg: WorkgroupWhereabouts) => (
                  <WhereaboutsGroupSection key={wg.workGroup.id} title={wg.workGroup.label} restricted={wg.restricted}>
                    {isScoped && (wg.whereabouts ?? []).length === 0
                      ? <EmptyTagView tag={wg.workGroup} />
                      : renderTeamBody(wg.whereabouts || [])}
                  </WhereaboutsGroupSection>
                ))}
              </>
            ) : (
              <EmptyState><BodyRegular>{t(emptyStateKey)}</BodyRegular></EmptyState>
            )}
          </ContentLayer>
      </Container>
    </CheckinConfirmationsContext.Provider>
  );
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
`;
